import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { ProcessorDetails } from '@/lib/api/processors'
import { ProcessorHeader } from '../components/processor-header'
import { ProcessorOverviewCard } from '../components/processor-overview-card'
import { ProcessorMetricsCard } from '../components/processor-metrics-card'
import { ProcessorPaymentMethodsCard } from '../components/processor-payment-methods-card'
import { NMI3DSCard } from '../nmi/components/nmi-3ds-card'

type Props = {
  processor: ProcessorDetails
}

const MASKED = '••••••••••••••••'

const readString = (options: Record<string, unknown> | null | undefined, key: string): string | undefined => {
  const value = options?.[key]
  return typeof value === 'string' && value !== '' ? value : undefined
}

const NetValveConfigurationCard = ({ processor }: Props) => {
  const { t } = useTranslation('processors')
  const options = processor.options
  const midId = readString(options, 'netvalveMidId')
  const siteId = readString(options, 'siteId')
  const isTestMode = options?.testMode === true
  const routingLabel = midId ? t('netvalve.credentials.midIdLabel') : t('netvalve.credentials.siteIdLabel')

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle>{t('netvalve.configurationTitle')}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div>
            <p className="text-sm text-muted-foreground">{t('apiKey')}</p>
            <p className="font-mono text-sm">{MASKED}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">{t('clientId')}</p>
            <p className="font-mono text-sm">{MASKED}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">{routingLabel}</p>
            <p className="font-mono text-sm">
              {midId ?? siteId ?? <span className="italic text-muted-foreground">{t('notConfigured')}</span>}
            </p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">{t('environment')}</p>
            <Badge variant={isTestMode ? 'outline' : 'default'}>{isTestMode ? t('sandbox') : t('production')}</Badge>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

export const NetValveProcessorDetailsPage = ({ processor }: Props) => (
  <>
    <ProcessorHeader type="netvalve" processor={processor} />
    <ProcessorOverviewCard processor={processor} />
    <ProcessorMetricsCard processor={processor} />
    <NetValveConfigurationCard processor={processor} />
    <NMI3DSCard processor={processor} />
    <ProcessorPaymentMethodsCard processor={processor} />
  </>
)
